import { Component, OnInit, OnDestroy, inject, input, model, output, effect, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, Subject, Subscription, catchError, defer, map, of, switchMap } from 'rxjs';
import {
  ApplicationTemplateDto,
  Form,
  FormApiService,
  FormTemplateType,
  FormStatus,
  FormStore,
  QuestionNumberingService,
  QuestionView,
  formSummaryFor
} from '@formrails/form-builder/data-access';
import { ApplicationTemplateMapper, ClonedFromDto } from '@formrails/shared/data-access';
import { CopyButtonComponent, DrawerResizeDirective } from '@formrails/shared/ui';
import { PreviewSectionsComponent } from '../preview-sections/preview-sections.component';

/** A form loaded for the preview pane, with questions ready to display. */
interface FormPreview {
  form: Form;
  questions: QuestionView[];
}

/** What the preview pane is showing for the selected form. */
type PreviewState =
  | { kind: 'empty' }
  | { kind: 'loading' }
  | { kind: 'loaded'; preview: FormPreview }
  | { kind: 'error'; message: string };

/**
 * Modal component for selecting existing forms. Split view: the list on the
 * left, and a read-only preview of the selected form on the right.
 * Uses Angular signals for inputs and outputs.
 */
@Component({
  selector: 'app-form-selector-modal',
  standalone: true,
  imports: [
    FormsModule,
    AsyncPipe,
    PreviewSectionsComponent,
    DrawerResizeDirective,
    CopyButtonComponent
  ],
  templateUrl: './form-selector-modal.component.html',
  styleUrl: './form-selector-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormSelectorModalComponent implements OnInit, OnDestroy {
  // Two-way binding for visibility
  readonly visible = model(false);

  readonly formSelected = output<ApplicationTemplateDto>();
  readonly formCloned = output<ApplicationTemplateDto>();
  /**
   * 'compare' picks a form to show beside the open one: the footer offers
   * Compare instead of Edit and Clone.
   */
  readonly mode = input<'open' | 'compare'>('open');
  readonly formCompared = output<ApplicationTemplateDto>();

  private formStore = inject(FormStore);
  private formApi = inject(FormApiService);
  private questionNumbering = inject(QuestionNumberingService);
  private subscriptions = new Subscription();

  /** Preview of the selected form, loaded on selection. */
  readonly preview = signal<PreviewState>({ kind: 'empty' });
  readonly loadedPreview = computed(() => {
    const state = this.preview();
    return state.kind === 'loaded' ? state.preview : null;
  });
  readonly previewError = computed(() => {
    const state = this.preview();
    return state.kind === 'error' ? state.message : null;
  });
  /** Read off the loaded form, not the list entry's totals, so the counts cannot disagree. */
  readonly previewCounts = computed(() => {
    const preview = this.loadedPreview();
    return preview ? formSummaryFor(preview.questions) : null;
  });
  /** Selected template ids to preview; switchMap drops superseded requests. */
  private readonly previewRequests = new Subject<string>();
  /** Previews already loaded while the modal is open, so reselecting is instant. */
  private readonly previewCache = new Map<string, FormPreview>();

  // Observable views derived from the signal store (keeps the async-pipe template contract)
  templates$: Observable<ApplicationTemplateDto[]> = toObservable(this.formStore.forms);
  loading$: Observable<boolean> = toObservable(this.formStore.loadingForms);
  error$: Observable<string | null> = toObservable(this.formStore.formsError);

  // Local component state
  templates: ApplicationTemplateDto[] = [];
  filteredTemplates: ApplicationTemplateDto[] = [];
  searchText: string = '';
  selectedStatus: string = '';
  selectedFormType: FormTemplateType | '' = 'Application'; // Default to Application
  selectedTemplate: ApplicationTemplateDto | null = null;

  FormStatus = FormStatus;

  // Form type options for the high-level filter (default: Application)
  formTypeOptions: { value: FormTemplateType | '', label: string }[] = [
    { value: '', label: 'All Types' },
    { value: 'Application', label: 'Application' },
    { value: 'Assessment', label: 'Assessment' },
    { value: 'EligibilityQuestionnaire', label: 'Eligibility Questionnaire' },
    { value: 'PreAssessment', label: 'Pre-assessment' },
    { value: 'Survey', label: 'Survey' },
    { value: 'UserProfile', label: 'User Profile' }
  ];

  // Status options for the filter dropdown (alphabetically ordered)
  statusOptions = [
    { value: '', label: 'All Statuses' },

    { value: 'Archived', label: 'Archived' },
    { value: 'Draft', label: 'Draft' },

    { value: 'Published', label: 'Published' }
  ];

  constructor() {
    // Effect to handle visibility changes
    effect(() => {
      const isVisible = this.visible();
      if (isVisible) {
        this.loadForms();
      } else {
        this.reset();
      }
    });
  }

  ngOnInit(): void {
    // Load the preview for each selection. Clicking quickly through the list
    // cancels the previous request, so a slow response can't overwrite a
    // newer selection.
    this.subscriptions.add(
      this.previewRequests
        .pipe(
          switchMap(templateId => {
            // An empty id cancels any request still in flight (used on reset).
            if (!templateId) {
              return of<PreviewState>({ kind: 'empty' });
            }
            const cached = this.previewCache.get(templateId);
            if (cached) {
              return of<PreviewState>({ kind: 'loaded', preview: cached });
            }
            // defer() turns a synchronous throw into an error the catchError
            // below handles; otherwise it would end this stream for good.
            return defer(() => this.formApi.getFormByTemplateId(templateId)).pipe(
              map(dto => {
                const preview = this.toPreview(ApplicationTemplateMapper.toForm(dto));
                this.previewCache.set(templateId, preview);
                return { kind: 'loaded', preview } as PreviewState;
              }),
              catchError(() =>
                of<PreviewState>({ kind: 'error', message: 'This form could not be loaded.' })
              )
            );
          })
        )
        .subscribe(state => this.preview.set(state))
    );

    // Subscribe to templates from store
    this.subscriptions.add(
      this.templates$.subscribe(templates => {
        this.templates = templates;
        // Apply filters to ensure the default form type filter is applied
        this.applyFilters();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadForms(): void {
    // Trigger the store loader (the rxMethod handles the API call)
    this.formStore.loadForms();
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  onFormTypeChange(formType: FormTemplateType | ''): void {
    this.selectedFormType = formType;
    this.applyFilters();
  }

  onStatusChipClick(status: string): void {
    this.selectedStatus = status;
    this.applyFilters();
  }

  private applyFilters(): void {
    let filtered = this.templates;

    // Apply form type filter (high-level filter)
    if (this.selectedFormType) {
      filtered = filtered.filter(template => template.formTemplateType === this.selectedFormType);
    }

    // Apply status filter
    if (this.selectedStatus) {
      filtered = filtered.filter(template => template.status === this.selectedStatus);
    }

    // Apply text search filter
    if (this.searchText.trim()) {
      const searchLower = this.searchText.toLowerCase().trim();
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(searchLower) ||
        template.displayName.toLowerCase().includes(searchLower) ||
        template.id.toLowerCase().includes(searchLower)
      );
    }

    this.filteredTemplates = filtered;
  }

  selectForm(template: ApplicationTemplateDto): void {
    if (this.selectedTemplate?.id === template.id) return;
    this.selectedTemplate = template;
    this.loadPreview(template.id);
  }

  onRetryPreview(): void {
    if (this.selectedTemplate) {
      this.loadPreview(this.selectedTemplate.id);
    }
  }

  private loadPreview(templateId: string): void {
    if (!this.previewCache.has(templateId)) {
      this.preview.set({ kind: 'loading' });
    }
    this.previewRequests.next(templateId);
  }

  /**
   * Match what the builder shows: the mapper's order (sections sit before
   * their first question), numbered, and not in edit mode.
   */
  private toPreview(form: Form): FormPreview {
    const questions = (form.questions ?? []).map(question => ({
      ...question,
      isEditing: false,
      showMenu: false
    }));
    return { form, questions: this.questionNumbering.recalculateQuestionNumbers(questions) };
  }

  onEditForm(): void {
    if (this.selectedTemplate) {
      this.formSelected.emit(this.selectedTemplate);
      this.close();
    }
  }

  /**
   * What the clone bubble says. The name and version are the source's as they
   * were when the copy was made, so this reads correctly even once the source
   * has moved on — or gone. Falls back to the id when the API could not read
   * the source at the time.
   */
  cloneSummary(clonedFrom: ClonedFromDto): string {
    const source = clonedFrom.name
      ? `${clonedFrom.name}${clonedFrom.version ? ` v${clonedFrom.version}` : ''}`
      : clonedFrom.id;
    const when = clonedFrom.clonedOn
      ? ` on ${new Date(clonedFrom.clonedOn).toLocaleDateString()}`
      : '';

    return `Cloned from ${source}${when}`;
  }

  onCompareForm(): void {
    if (this.selectedTemplate) {
      this.formCompared.emit(this.selectedTemplate);
      this.close();
    }
  }

  onCloneForm(): void {
    if (this.selectedTemplate) {
      this.formCloned.emit(this.selectedTemplate);
      this.close();
    }
  }

  /** Close the drawer without choosing a form. */
  onCancel(): void {
    this.close();
  }

  close(): void {
    this.visible.set(false);
    this.reset();
  }

  private reset(): void {
    this.searchText = '';
    this.selectedStatus = '';
    this.selectedFormType = 'Application'; // Reset to default
    this.selectedTemplate = null;
    // Forms may be edited between visits, so don't keep previews across opens.
    this.previewRequests.next('');
    this.previewCache.clear();
  }
}

